import { useState } from 'react'

import { SplitSupplyDayType } from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  FieldRequiredIndicator,
  FieldRoot,
  HStack,
  Input,
  OutlineDangerButton,
  PrimaryButton,
  SimpleGrid,
  Stack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import { DsSelect } from '~/components/common/DsSelect'
import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { ToggleButton } from '~/components/common/ToggleButton'
import { SlotValueGrid } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/components/SlotValueGrid'
import {
  type BasePatternValue,
  DAY_TYPES,
  type PatternDraft,
  type PatternDraftIssue,
  validatePatternDraft,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/basePattern'
import { dayTypeLabel } from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/labels'
import {
  SLOT_END_OPTIONS,
  SLOT_START_OPTIONS,
  slotStartLabel,
} from '~/pages/AdminOcctoMaster/tabs/SplitSupplyContract/models/slotTime'

const NS = 'page:adminOcctoMaster.splitSupplyContract.basePattern' as const
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1)

type Props = {
  mode: 'create' | 'edit'
  initial: PatternDraft
  /** 同じ契約の他の基本パターンの適用優先順 (編集中の自分は含めない)。 */
  otherOrders: readonly number[]
  onSubmit: (value: BasePatternValue) => void
  onDelete?: () => void
  onClose: () => void
}

/**
 * 基本パターンの追加・編集モーダル (契約モーダルに重ねて開く)。
 * OK で契約フォームの基本パターン一覧に反映するだけで、 保存は契約の「保存」で一緒に行う。
 * 値は「全コマ同値」か「48 コマの表」(Excel から貼り付け可) で入れる。
 * 検査は backend と同じ理由で事前に行い、 OK できない理由を欄脇に出す。
 */
export const BasePatternModal = ({
  mode,
  initial,
  otherOrders,
  onSubmit,
  onDelete,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<PatternDraft>(initial)
  const [issues, setIssues] = useState<PatternDraftIssue[]>([])

  const patch = (next: Partial<PatternDraft>): void => {
    setDraft((prev) => ({ ...prev, ...next }))
  }

  const handleOk = (): void => {
    const result = validatePatternDraft(draft, otherOrders)
    if (result.ok) {
      setIssues([])
      onSubmit(result.value)
      return
    }
    setIssues(result.issues)
  }

  const issueOf = <K extends PatternDraftIssue['kind']>(
    kind: K,
  ): Extract<PatternDraftIssue, { kind: K }> | undefined =>
    issues.find(
      (i): i is Extract<PatternDraftIssue, { kind: K }> => i.kind === kind,
    )

  const toggleDayType = (day: SplitSupplyDayType): void => {
    patch({
      dayTypes: draft.dayTypes.includes(day)
        ? draft.dayTypes.filter((d) => d !== day)
        : [...draft.dayTypes, day],
    })
  }
  const toggleMonth = (month: number): void => {
    patch({
      months: draft.months.includes(month)
        ? draft.months.filter((m) => m !== month)
        : [...draft.months, month],
    })
  }

  const slotFromIndex = draft.slotFrom === '' ? 0 : Number(draft.slotFrom)
  const endOptions = SLOT_END_OPTIONS.filter(
    (o) => Number(o.value) >= slotFromIndex,
  )

  const orderIssue =
    issueOf('orderInvalid') ??
    issueOf('orderTooLarge') ??
    issueOf('orderDuplicated')
  const slotValueIssue = issueOf('slotValueInvalid')

  const footer = (
    <HStack justify="space-between" width="100%">
      {mode === 'edit' && onDelete !== undefined ? (
        <OutlineDangerButton onClick={onDelete}>
          {t('common:action.delete')}
        </OutlineDangerButton>
      ) : (
        <span />
      )}
      <HStack gap={2}>
        <TertiaryButton onClick={onClose}>
          {t('common:action.cancel')}
        </TertiaryButton>
        <PrimaryButton onClick={handleOk}>
          {t('common:action.ok')}
        </PrimaryButton>
      </HStack>
    </HStack>
  )

  return (
    <Modal
      footer={footer}
      isOpen
      onClose={onClose}
      scrollBehavior="inside"
      size="lg"
      title={t(
        `${NS}.modal.${mode === 'create' ? 'createTitle' : 'editTitle'}`,
      )}
    >
      <VStack align="stretch" gap={4}>
        <SimpleGrid gap={3} columns={3}>
          <FieldRoot invalid={orderIssue !== undefined} required>
            <FieldLabel htmlFor="split-supply-pattern-order">
              {t(`${NS}.modal.order`)}
              <FieldRequiredIndicator />
            </FieldLabel>
            <Input
              id="split-supply-pattern-order"
              inputMode="numeric"
              onChange={(e) => {
                patch({ patternOrder: e.target.value })
              }}
              value={draft.patternOrder}
            />
            {orderIssue?.kind === 'orderTooLarge' && (
              <FieldErrorText>{t(`${NS}.error.orderTooLarge`)}</FieldErrorText>
            )}
            {orderIssue?.kind === 'orderInvalid' && (
              <FieldErrorText>{t(`${NS}.error.orderInvalid`)}</FieldErrorText>
            )}
            {orderIssue?.kind === 'orderDuplicated' && (
              <FieldErrorText>
                {t(`${NS}.error.orderDuplicated`, { order: orderIssue.order })}
              </FieldErrorText>
            )}
          </FieldRoot>
          <FieldRoot required>
            <FieldLabel htmlFor="split-supply-pattern-validFrom">
              {t(`${NS}.modal.validFrom`)}
              <FieldRequiredIndicator />
            </FieldLabel>
            <CompactCivilDateField
              id="split-supply-pattern-validFrom"
              onChange={(d) => {
                if (d !== undefined) patch({ validFrom: d })
              }}
              value={draft.validFrom}
            />
          </FieldRoot>
          <FieldRoot invalid={issueOf('periodInvalid') !== undefined}>
            <FieldLabel htmlFor="split-supply-pattern-validTo">
              {t(`${NS}.modal.validTo`)}
            </FieldLabel>
            <CompactCivilDateField
              hasClear
              hasError={issueOf('periodInvalid') !== undefined}
              id="split-supply-pattern-validTo"
              onChange={(d) => {
                patch({ validTo: d })
              }}
              value={draft.validTo}
            />
            {issueOf('periodInvalid') !== undefined && (
              <FieldErrorText>{t(`${NS}.error.periodInvalid`)}</FieldErrorText>
            )}
          </FieldRoot>
        </SimpleGrid>

        <FieldRoot invalid={issueOf('dayTypesRequired') !== undefined}>
          <FieldLabel>{t(`${NS}.modal.dayTypes`)}</FieldLabel>
          <HStack gap={2}>
            {DAY_TYPES.map((day) => (
              <ToggleButton
                key={day}
                onClick={() => {
                  toggleDayType(day)
                }}
                pressed={draft.dayTypes.includes(day)}
                size="sm"
              >
                {dayTypeLabel(t, day)}
              </ToggleButton>
            ))}
          </HStack>
          {issueOf('dayTypesRequired') !== undefined && (
            <FieldErrorText>{t(`${NS}.error.dayTypesRequired`)}</FieldErrorText>
          )}
        </FieldRoot>

        <FieldRoot>
          <FieldLabel>{t(`${NS}.modal.months`)}</FieldLabel>
          <HStack flexWrap="wrap" gap={2}>
            {MONTHS.map((month) => (
              <ToggleButton
                key={month}
                aria-label={t(`${NS}.modal.monthAria`, { month })}
                onClick={() => {
                  toggleMonth(month)
                }}
                pressed={draft.months.includes(month)}
                size="sm"
              >
                {month}
              </ToggleButton>
            ))}
          </HStack>
        </FieldRoot>

        <SimpleGrid gap={3} columns={2}>
          <FieldRoot invalid={issueOf('slotRangeInvalid') !== undefined}>
            <FieldLabel>{t(`${NS}.modal.slotFrom`)}</FieldLabel>
            <DsSelect
              aria-label={t(`${NS}.modal.slotFrom`)}
              onChange={(v) => {
                const from = v ?? ''
                patch({
                  slotFrom: from,
                  // 開始より前の終了は選べないので空 (終日の端) に戻す
                  slotTo:
                    from !== '' &&
                    draft.slotTo !== '' &&
                    Number(draft.slotTo) < Number(from)
                      ? ''
                      : draft.slotTo,
                })
              }}
              options={SLOT_START_OPTIONS}
              placeholder={t(`${NS}.modal.allDayStart`)}
              value={draft.slotFrom === '' ? undefined : draft.slotFrom}
            />
          </FieldRoot>
          <FieldRoot invalid={issueOf('slotRangeInvalid') !== undefined}>
            <FieldLabel>{t(`${NS}.modal.slotTo`)}</FieldLabel>
            <DsSelect
              aria-label={t(`${NS}.modal.slotTo`)}
              onChange={(v) => {
                patch({ slotTo: v ?? '' })
              }}
              options={endOptions}
              placeholder={t(`${NS}.modal.allDayEnd`)}
              value={draft.slotTo === '' ? undefined : draft.slotTo}
            />
            {issueOf('slotRangeInvalid') !== undefined && (
              <FieldErrorText>
                {t(`${NS}.error.slotRangeInvalid`)}
              </FieldErrorText>
            )}
          </FieldRoot>
        </SimpleGrid>

        <FieldRoot invalid={issueOf('uniformValueInvalid') !== undefined}>
          <FieldLabel>{t(`${NS}.modal.valueMode`)}</FieldLabel>
          <HStack gap={2}>
            <ToggleButton
              onClick={() => {
                patch({ valueMode: 'uniform' })
              }}
              pressed={draft.valueMode === 'uniform'}
              size="sm"
            >
              {t(`${NS}.modal.valueModeUniform`)}
            </ToggleButton>
            <ToggleButton
              onClick={() => {
                patch({ valueMode: 'perSlot' })
              }}
              pressed={draft.valueMode === 'perSlot'}
              size="sm"
            >
              {t(`${NS}.modal.valueModePerSlot`)}
            </ToggleButton>
          </HStack>
        </FieldRoot>

        {draft.valueMode === 'uniform' ? (
          <FieldRoot invalid={issueOf('uniformValueInvalid') !== undefined}>
            <FieldLabel htmlFor="split-supply-pattern-uniform">
              {t(`${NS}.modal.uniformValue`)}
            </FieldLabel>
            <Input
              id="split-supply-pattern-uniform"
              inputMode="numeric"
              onChange={(e) => {
                patch({ uniformValue: e.target.value })
              }}
              value={draft.uniformValue}
            />
            {issueOf('uniformValueInvalid') !== undefined && (
              <FieldErrorText>
                {t(`${NS}.error.uniformValueInvalid`)}
              </FieldErrorText>
            )}
          </FieldRoot>
        ) : (
          <Stack gap={2}>
            <Text textStyle="caption" color="Text.midEmphasis">
              {t(`${NS}.modal.pasteHint`)}
            </Text>
            {slotValueIssue !== undefined && (
              <Alert
                status="error"
                title={t(`${NS}.error.slotValueInvalid`, {
                  time: slotStartLabel(slotValueIssue.slotIndex),
                })}
              />
            )}
            <SlotValueGrid
              invalidIndex={slotValueIssue?.slotIndex}
              onChange={(next) => {
                patch({ slotValues: next })
              }}
              values={draft.slotValues}
            />
          </Stack>
        )}
      </VStack>
    </Modal>
  )
}
